import { useEffect, useState, useCallback, useMemo } from "react";
import { createPortal } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchActiveLanguages,
  fetchTranslations,
  setSelectedLanguage,
} from "../store/language/action";
import { baseURL } from "../util/Config";
import translateIcon from "../assets/images/translate.png";

const C = {
  overlay: "rgba(0, 0, 0, 0.55)",
  border: "rgba(255, 255, 255, 0.08)",
  pink: "#FF4D8D",
  gradient135: "linear-gradient(135deg, #FF2D92 0%, #8E2DE2 100%)",
};

const buildIconSrc = (iconPath) => {
  if (!iconPath) return null;
  if (/^https?:\/\//i.test(iconPath)) return iconPath;
  return `${baseURL || ""}${iconPath}`;
};

/** variant: "header" = chip in header; "silent" = fetch only, no UI */
const LanguageSwitcher = ({ variant = "header", embedded }) => {
  const dispatch = useDispatch();
  const {
    activeLanguages,
    selectedLanguageCode,
    selectedLanguage,
    loadingTranslations,
  } = useSelector((state) => state.language);

  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  // Support both BD `variant` and agency `embedded` prop from Admin
  const isSilent = variant === "silent";
  const showHeader = embedded || variant === "header";

  useEffect(() => {
    dispatch(fetchActiveLanguages());
  }, [dispatch]);

  useEffect(() => {
    if (!selectedLanguageCode) return;
    dispatch(fetchTranslations(selectedLanguageCode));
  }, [selectedLanguageCode, dispatch]);

  const handleClose = useCallback(() => {
    setOpen(false);
    setSearch("");
  }, []);

  const handleSelect = useCallback(
    (code) => {
      dispatch(setSelectedLanguage(code));
      handleClose();
    },
    [dispatch, handleClose],
  );

  const filteredLanguages = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = !q
      ? [...(activeLanguages || [])]
      : (activeLanguages || []).filter((lang) => {
          const name = (
            lang.localLanguageTitle ||
            lang.languageTitle ||
            ""
          ).toLowerCase();
          const code = (lang.languageCode || "").toLowerCase();
          return name.includes(q) || code.includes(q);
        });

    list.sort((a, b) => {
      const aSel =
        a.languageCode?.toLowerCase() === selectedLanguageCode?.toLowerCase()
          ? 0
          : 1;
      const bSel =
        b.languageCode?.toLowerCase() === selectedLanguageCode?.toLowerCase()
          ? 0
          : 1;
      return aSel - bSel;
    });
    return list;
  }, [activeLanguages, search, selectedLanguageCode]);

  if (isSilent || !showHeader) return null;

  const currentLabel =
    selectedLanguage?.localLanguageTitle ||
    selectedLanguage?.languageTitle ||
    selectedLanguageCode ||
    "English";

  const modal =
    open &&
    createPortal(
      <div
        style={{
          position: "fixed",
          inset: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 16,
          background: C.overlay,
          backdropFilter: "blur(8px)",
          WebkitBackdropFilter: "blur(8px)",
          zIndex: 99999,
        }}
        onClick={handleClose}
      >
        <div
          className="lang-modal"
          style={{
            width: "100%",
            maxWidth: 384,
            display: "flex",
            flexDirection: "column",
            background: "#381559",
            borderRadius: 20,
            border: "none",
            boxShadow: "none",
            maxHeight: "78vh",
            overflow: "hidden",
            fontFamily: '"Airbnb Cereal", sans-serif',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div
            style={{
              flexShrink: 0,
              position: "relative",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "14px 20px 12px",
              borderBottom: "1px solid #FFFFFF1A",
            }}
          >
            <h2
              style={{
                fontSize: 16,
                fontWeight: 700,
                color: "#FFFFFF",
                margin: 0,
              }}
            >
              Select Language
            </h2>
            <button
              type="button"
              onClick={handleClose}
              style={{
                position: "absolute",
                right: 16,
                top: "50%",
                transform: "translateY(-50%)",
                width: 32,
                height: 32,
                borderRadius: "50%",
                background: "rgba(255,255,255,0.08)",
                border: `1px solid ${C.border}`,
                color: "#FFFFFF",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: 0,
              }}
              aria-label="Close"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#FFFFFF"
                strokeWidth="2.5"
                strokeLinecap="round"
              >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>

          {/* Search + list */}
          <div
            style={{
              flex: 1,
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
              padding: "14px 16px 8px",
            }}
          >
            <div style={{ position: "relative", marginBottom: 14, flexShrink: 0 }}>
              <svg
                width={16}
                height={16}
                viewBox="0 0 24 24"
                fill="none"
                stroke="#FFFFFF80"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{
                  position: "absolute",
                  left: 14,
                  top: "50%",
                  transform: "translateY(-50%)",
                  pointerEvents: "none",
                }}
                aria-hidden="true"
              >
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search language by name & code"
                style={{
                  width: "100%",
                  height: 44,
                  padding: "0 16px 0 40px",
                  borderRadius: 10,
                  background: "#FFFFFF1A",
                  border: "0.5px solid #FFFFFF26",
                  color: "#FFFFFF",
                  fontSize: 13,
                  fontFamily: "inherit",
                  outline: "none",
                  boxSizing: "border-box",
                }}
              />
            </div>

            <div
              className="lang-modal-list"
              style={{
                flex: 1,
                minHeight: 0,
                overflowY: "auto",
                scrollbarWidth: "none",
                msOverflowStyle: "none",
              }}
            >
              {filteredLanguages.length === 0 ? (
                <p
                  style={{
                    color: "#FFFFFF80",
                    fontSize: 13,
                    textAlign: "center",
                    margin: "24px 0",
                  }}
                >
                  No languages found
                </p>
              ) : (
                filteredLanguages.map((lang) => {
                  const isActive =
                    lang.languageCode?.toLowerCase() ===
                    selectedLanguageCode?.toLowerCase();
                  const code = (lang.languageCode || "").toUpperCase();
                  const iconSrc = buildIconSrc(lang.languageIcon);
                  return (
                    <button
                      key={lang._id || lang.languageCode}
                      type="button"
                      onClick={() => handleSelect(lang.languageCode)}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 12,
                        width: "100%",
                        padding: "12px 12px",
                        marginBottom: 4,
                        borderRadius: 14,
                        border: isActive ? "none" : "0.5px solid transparent",
                        background: isActive
                          ? "linear-gradient(90deg, #FD297E 0%, #6B01FF 100%)"
                          : "transparent",
                        cursor: "pointer",
                        textAlign: "left",
                        fontFamily: "inherit",
                      }}
                    >
                      {iconSrc ? (
                        <img
                          src={iconSrc}
                          alt=""
                          style={{
                            width: 28,
                            height: 28,
                            borderRadius: "50%",
                            objectFit: "cover",
                            flexShrink: 0,
                          }}
                          onError={(e) => {
                            e.target.style.display = "none";
                          }}
                        />
                      ) : (
                        <span
                          style={{
                            width: 28,
                            height: 28,
                            borderRadius: "50%",
                            background: isActive
                              ? "rgba(255,255,255,0.2)"
                              : C.gradient135,
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: 10,
                            fontWeight: 700,
                            color: "#FFFFFF",
                            flexShrink: 0,
                          }}
                        >
                          {code.slice(0, 2)}
                        </span>
                      )}
                      <span
                        style={{
                          flex: 1,
                          fontSize: 14,
                          fontWeight: isActive ? 600 : 500,
                          color: "#FFFFFF",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {lang.localLanguageTitle || lang.languageTitle}
                      </span>
                      <span
                        style={{
                          fontSize: 12,
                          fontWeight: 500,
                          color: isActive ? "#FFFFFF" : "#FFFFFF80",
                          letterSpacing: "0.04em",
                          flexShrink: 0,
                        }}
                      >
                        {code}
                      </span>
                      {isActive && (
                        <span
                          style={{
                            width: 22,
                            height: 22,
                            borderRadius: "50%",
                            background: "#FFFFFF",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                          }}
                        >
                          <svg
                            width="13"
                            height="13"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="#6B01FF"
                            strokeWidth="3"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <polyline points="20 6 9 17 4 12" />
                          </svg>
                        </span>
                      )}
                    </button>
                  );
                })
              )}
            </div>
          </div>
        </div>

        <style>{`
          .lang-modal-list {
            scrollbar-width: none;
            -ms-overflow-style: none;
          }
          .lang-modal-list::-webkit-scrollbar {
            display: none;
            width: 0;
            height: 0;
          }
          .lang-modal input::placeholder {
            color: #FFFFFF80;
            opacity: 1;
          }
        `}</style>
      </div>,
      document.body,
    );

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 8,
          padding: "8px 14px",
          background: "#FFFFFF1A",
          border: "1px solid #FFFFFF26",
          borderRadius: 12,
          cursor: "pointer",
          color: "#FFFFFF",
          fontSize: 14,
          fontWeight: 500,
          whiteSpace: "nowrap",
          flexShrink: 0,
          fontFamily: '"Airbnb Cereal", sans-serif',
        }}
      >
        <img
          src={translateIcon}
          alt=""
          style={{
            width: 18,
            height: 18,
            objectFit: "contain",
            display: "block",
            flexShrink: 0,
          }}
        />
        <span style={{ color: "#FFFFFF" }}>{currentLabel}</span>
        {loadingTranslations ? (
          <span
            style={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              background: C.pink,
              display: "inline-block",
            }}
          />
        ) : (
          <svg
            width={14}
            height={14}
            viewBox="0 0 24 24"
            fill="none"
            stroke="#FFFFFF"
            strokeWidth="2.5"
            style={{ flexShrink: 0 }}
          >
            <polyline points="6 9 12 15 18 9" />
          </svg>
        )}
      </button>
      {modal}
    </>
  );
};

export default LanguageSwitcher;
